import {verifiedFetch} from '@github-ui/verified-fetch'

/**
 * Request access to Copilot cloud agent for a member in an organization
 *
 * @param orgLogin - The organization login/slug
 * @returns Promise that resolves when the request is created
 * @throws Error if the request fails
 *
 * @example
 * ```ts
 * await requestCopilotCodingAgentAccess('github')
 * ```
 */
export async function requestCopilotCodingAgentAccess(orgLogin: string): Promise<void> {
  const response = await verifiedFetch(`/orgs/${orgLogin}/member_feature_request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      feature: 'copilot_coding_agent',
    }),
  })

  if (!response.ok) {
    let errorData = {}
    try {
      errorData = await response.json()
    } catch {
      // Ignore JSON parsing errors
    }
    throw new Error((errorData as {error?: string}).error || 'Failed to create request')
  }
}

/**
 * Remove a Copilot cloud agent access request for a member in an organization
 *
 * @param orgLogin - The organization login/slug
 * @returns Promise that resolves when the request is removed
 * @throws Error if the removal fails
 *
 * @example
 * ```ts
 * await removeCopilotCodingAgentRequest('github')
 * ```
 */
export async function removeCopilotCodingAgentRequest(orgLogin: string): Promise<void> {
  const response = await verifiedFetch(`/orgs/${orgLogin}/member_feature_request?feature=copilot_coding_agent`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    throw new Error('Failed to cancel request')
  }
}

/**
 * Enable Copilot cloud agent policy for an organization
 *
 * @param orgLogin - The organization login/slug
 * @returns Promise that resolves when the policy is enabled
 * @throws Error if enabling fails
 *
 * @example
 * ```ts
 * await enableCopilotCodingAgentPolicy('github')
 * ```
 */
export async function enableCopilotCodingAgentPolicy(orgLogin: string): Promise<void> {
  const response = await verifiedFetch(`/organizations/${orgLogin}/settings/copilot/policies`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      swe_agent: 'enabled',
    }),
  })

  // Check if we got a successful response
  if (!response.ok) {
    let errorData = {}
    try {
      errorData = await response.json()
    } catch {
      // Ignore JSON parsing errors
    }
    throw new Error((errorData as {message?: string}).message || 'Failed to enable feature')
  }
}
